"use client";

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

interface SpecialInfo {
  id: string;
  name: string;
  type: "SALE" | "OFFER";
  discount?: string;
  price?: string;
  // Parsed deal format for display (e.g., "2 for $75")
  dealFormat?: string;
}

interface SpecialsContextType {
  // Map of productId -> special info (for quick lookup)
  bundleProducts: Map<string, SpecialInfo>;
  saleProducts: Map<string, SpecialInfo>;
  isLoading: boolean;
  // Helper to check if a product has a bundle deal
  getBundleDeal: (productId: string) => SpecialInfo | null;
  // Helper to check if product is on sale
  getSaleDeal: (productId: string) => SpecialInfo | null;
}

const SpecialsContext = createContext<SpecialsContextType | null>(null);

// Dutchie API constants
const DUTCHIE_API_URL = "https://plus.dutchie.com/plus/2021-07/graphql";
const RETAILER_ID = "94626601-56a5-4764-a8ef-be47fa946bae";
const API_KEY = "Bearer secret-eyJhbGciOiJIUzI1NiJ9.eyJhdWQiOiJBUEktQ0xJRU5UIiwiZXhwIjozMzI1MzI2NzQyMiwiaWF0IjoxNjk2MzU4NjIyLCJpc3MiOiJodHRwczovL2R1dGNoaWUuY29tIiwianRpIjoiMmIwZTc0NWMtOGUyYi00YjZkLTg4OGQtMzE1MzgyMzI2MjllIiwiZW50ZXJwcmlzZV9pZCI6ImFlYWU1ZTViLWE4YWQtNGFjOS04NWYyLWUxZDdkNDg1N2E4ZCIsInV1aWQiOiI1NGU5NzE1NC0zYThjLTQzOTctODJiZC00NDJhNjJlOWY5MjUifQ.Xjf17z9OnvZEVPwjsbx8I7GW8TS8L3oLvFvDYlRmw-4";

export function SpecialsProvider({ children }: { children: ReactNode }) {
  const [bundleProducts, setBundleProducts] = useState<Map<string, SpecialInfo>>(new Map());
  const [saleProducts, setSaleProducts] = useState<Map<string, SpecialInfo>>(new Map());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchSpecialsData() {
      try {
        // Step 1: Fetch all specials
        const specialsRes = await fetch(DUTCHIE_API_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: API_KEY,
          },
          body: JSON.stringify({
            query: `{ specials(retailerId: "${RETAILER_ID}") { id name type } }`
          }),
        });

        const specialsData = await specialsRes.json();
        const specials = specialsData.data?.specials || [];

        const newBundleProducts = new Map<string, SpecialInfo>();
        const newSaleProducts = new Map<string, SpecialInfo>();

        // Step 2: For each special, fetch its products
        for (const special of specials) {
          const productsRes = await fetch(DUTCHIE_API_URL, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: API_KEY,
            },
            body: JSON.stringify({
              query: `{ 
                menu(retailerId: "${RETAILER_ID}", filter: { menuSection: { type: SPECIALS, specialId: "${special.id}" }}) { 
                  products { id } 
                } 
              }`
            }),
          });

          const productsData = await productsRes.json();
          const products = productsData.data?.menu?.products || [];

          // Parse special name for display info
          const priceMatch = special.name.match(/\$(\d+)/);
          const discountMatch = special.name.match(/(\d+)%/);
          
          // Parse deal format dynamically from name
          // Examples: "MFNY - 2 510 VAPES FOR $75" -> "2 for $75"
          //           "Buy 3 Get 1 Free" -> "Buy 3 Get 1"
          //           "20% OFF" -> "20% OFF"
          let dealFormat: string | undefined;
          
          // Pattern: "X [items] FOR $Y" or "X FOR $Y"
          const xForYMatch = special.name.match(/(\d+)\s+(?:\w+\s+)*FOR\s+\$(\d+)/i);
          if (xForYMatch) {
            dealFormat = `${xForYMatch[1]} for $${xForYMatch[2]}`;
          }
          // Pattern: "Buy X Get Y" 
          else if (/buy\s+\d+\s+get/i.test(special.name)) {
            const buyGetMatch = special.name.match(/buy\s+(\d+)\s+get\s+(\d+)/i);
            if (buyGetMatch) {
              dealFormat = `Buy ${buyGetMatch[1]} Get ${buyGetMatch[2]}`;
            }
          }
          // Pattern: "X% OFF"
          else if (discountMatch) {
            dealFormat = `${discountMatch[1]}% OFF`;
          }
          // Fallback: just show the price if found
          else if (priceMatch) {
            dealFormat = `$${priceMatch[1]}`;
          }
          
          const specialInfo: SpecialInfo = {
            id: special.id,
            name: special.name,
            type: special.type,
            price: priceMatch ? `$${priceMatch[1]}` : undefined,
            discount: discountMatch ? `${discountMatch[1]}%` : undefined,
            dealFormat,
          };

          // Map each product to its special
          for (const product of products) {
            if (special.type === "OFFER") {
              // Bundle deal
              newBundleProducts.set(product.id, specialInfo);
            } else if (special.type === "SALE") {
              // Sale
              newSaleProducts.set(product.id, specialInfo);
            }
          }
        }

        setBundleProducts(newBundleProducts);
        setSaleProducts(newSaleProducts);
      } catch (error) {
        console.error("Failed to fetch specials data:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchSpecialsData();
  }, []);

  const getBundleDeal = (productId: string): SpecialInfo | null => {
    return bundleProducts.get(productId) || null;
  };

  const getSaleDeal = (productId: string): SpecialInfo | null => {
    return saleProducts.get(productId) || null;
  };

  return (
    <SpecialsContext.Provider value={{ 
      bundleProducts, 
      saleProducts, 
      isLoading, 
      getBundleDeal,
      getSaleDeal 
    }}>
      {children}
    </SpecialsContext.Provider>
  );
}

export function useSpecials() {
  const context = useContext(SpecialsContext);
  if (!context) {
    // Return a default value if not wrapped in provider (graceful fallback)
    return {
      bundleProducts: new Map(),
      saleProducts: new Map(),
      isLoading: false,
      getBundleDeal: () => null,
      getSaleDeal: () => null,
    };
  }
  return context;
}
